// Game library card component for the library page

import React, { useEffect, useRef } from 'react';
import { InfoIcon, XIcon } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { GameStatsRow } from '@/components/games';
import { useMedia } from '../../../hooks';
import { analyticsService } from '../../../services/analyticsService';
import { ASSETS } from '@airhead-games/shared/configs';
import type { GameLibraryConfig } from '@airhead-games/shared/types';
import { getGameCoverAssetKey } from '@airhead-games/shared/utilities';

interface GameLibraryCardProps {
  game: GameLibraryConfig;
  infoOpen: boolean;
  revealed: boolean;
  onToggleInfo: () => void;
  onRevealEnter: () => void;
  onRevealLeave: () => void;
  onStart: (game: GameLibraryConfig) => void;
}

const GameLibraryCard: React.FC<GameLibraryCardProps> = ({
  game,
  infoOpen,
  revealed,
  onToggleInfo,
  onRevealEnter,
  onRevealLeave,
  onStart,
}) => {
  const { loadImageFromUrl } = useMedia();
  const coverAssetKey = getGameCoverAssetKey(game.gameCore.gameId);
  const imageUrl = coverAssetKey ? ASSETS[coverAssetKey] : undefined;
  const loonCost = game.gameCore.loonCost ?? 0;
  const gameDescription = game.gameCore.gameDescription.replace(/\\n/g, '\n');

  // Split name so the last word and info icon stay glued together on wrap.
  const nameWords = game.gameCore.gameName.split(' ');
  const nameLastWord = nameWords.pop() ?? game.gameCore.gameName;
  const nameLead = nameWords.join(' ');

  useEffect(() => {
    if (!imageUrl) {
      return;
    }

    loadImageFromUrl(imageUrl).catch((error) => {
      console.warn(
        `Failed to load game cover for ${game.gameCore.gameName}:`,
        error
      );
    });
  }, [game.gameCore.gameName, imageUrl, loadImageFromUrl]);

  // Tracks whether a real mouse is currently over the card. Touch is ignored
  // (iOS fires synthetic mouse events that would mis-set this).
  const mouseOverRef = useRef(false);

  const toggleInfo = () => {
    if (!infoOpen) {
      analyticsService.trackButtonClick('View Game', {
        game_id: game.gameCore.gameId,
        game_name: game.gameCore.gameName,
        link_location: 'library',
      });
      // Opening — make sure reveal follows (needed on touch, where pointerenter
      // was skipped and revealedGameId isn't set yet).
      onRevealEnter();
      onToggleInfo();
    } else {
      onToggleInfo();
      // Closing — drop reveal only if the mouse isn't still hovering the card.
      // Otherwise the user loses their hover state and has to re-enter.
      if (!mouseOverRef.current) {
        onRevealLeave();
      }
    }
  };

  const handleInfoToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (infoOpen) {
      queueMicrotask(() => {
        event.currentTarget.blur();
      });
    }
    toggleInfo();
  };

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Let nested buttons handle their own clicks.
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    toggleInfo();
  };

  const handleStartClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onStart(game);
  };

  return (
    <Card
      tabIndex={0}
      role="group"
      aria-label={game.gameCore.gameName}
      data-info-open={infoOpen ? 'true' : undefined}
      data-revealed={revealed ? 'true' : undefined}
      onClick={handleCardClick}
      onPointerEnter={(event) => {
        // Skip touch — iOS fires synthetic mouseenter on first tap and
        // suppresses the click, producing a two-tap "hover then click" bug.
        if (event.pointerType !== 'mouse') return;
        mouseOverRef.current = true;
        onRevealEnter();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType !== 'mouse') return;
        mouseOverRef.current = false;
        onRevealLeave();
      }}
      onFocus={onRevealEnter}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          onRevealLeave();
        }
      }}
      className="group/card w-full gap-0 overflow-visible p-0 py-0 shadow-sm ring-1 ring-foreground/10 transition-[transform,box-shadow] duration-200 ease-out focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 data-[revealed=true]:shadow-md"
    >
      <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden rounded-t-[inherit] bg-muted">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 size-full object-cover transition-transform duration-200 ease-out group-data-[revealed=true]/card:scale-[1.02]"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-sm text-muted-foreground">
            Cover coming soon
          </div>
        )}

        {infoOpen && (
          <div
            className="absolute inset-0 z-10 overflow-y-auto bg-gradient-to-b from-black/75 via-black/82 to-black/92 p-3 pr-12 text-left text-sm leading-relaxed text-white"
            role="region"
            aria-label="Game description"
          >
            <p className="whitespace-pre-line">{gameDescription}</p>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Hide description for ${game.gameCore.gameName}`}
              onClick={(event) => {
                event.stopPropagation();
                toggleInfo();
              }}
              className="!absolute !top-2 !right-2 z-20 !size-8 !min-h-8 !min-w-8 !p-0 text-white hover:bg-white/15 hover:text-white focus-visible:bg-white/20 focus-visible:text-white md:hidden"
            >
              <XIcon className="size-4" aria-hidden="true" />
            </Button>
          </div>
        )}
      </div>

      <div className="flex h-16 items-center gap-3 px-4 md:h-14 md:gap-4">
        <div className="min-w-0 flex-1 overflow-hidden">
          <h3 className="w-full overflow-hidden text-left font-sans text-sm leading-tight font-semibold tracking-tight break-words text-card-foreground line-clamp-2 md:text-base">
            {nameLead && <>{nameLead} </>}
            <span className="whitespace-nowrap">
              {nameLastWord}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-pressed={infoOpen}
                aria-label={
                  infoOpen
                    ? `Hide description for ${game.gameCore.gameName}`
                    : `Show description for ${game.gameCore.gameName}`
                }
                onClick={handleInfoToggle}
                className={`hidden !size-6 !min-h-6 !min-w-6 !p-0 ml-1 align-middle transition-opacity md:inline-flex md:opacity-0 md:group-data-[revealed=true]/card:opacity-100 ${
                  infoOpen
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <InfoIcon className="size-4" aria-hidden="true" />
              </Button>
            </span>
          </h3>
        </div>

        {/* Fixed-width stacked layers: stats by default, Select game when card is revealed */}
        <div className="relative h-11 w-[170px] shrink-0 md:w-[190px]">
          <div
            className="pointer-events-none absolute inset-0 flex flex-nowrap items-center justify-end gap-x-2.5 overflow-hidden text-sm text-muted-foreground opacity-100 transition-opacity duration-150 group-data-[revealed=true]/card:opacity-0"
            aria-hidden="true"
          >
            <GameStatsRow
              gamePlayersMin={game.gameCore.gamePlayersMin}
              gamePlayersMax={game.gameCore.gamePlayersMax}
              averageDuration={game.gameWiki.averageDuration}
              loonCost={loonCost}
            />
          </div>

          <div className="pointer-events-none absolute inset-0 flex items-center justify-end opacity-0 transition-opacity duration-150 group-data-[revealed=true]/card:pointer-events-auto group-data-[revealed=true]/card:opacity-100">
            <Button
              type="button"
              size="xl"
              className="h-11 shrink-0 px-5"
              onClick={handleStartClick}
            >
              Select game
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default GameLibraryCard;
